import { useEffect, useRef, useState, Activity } from 'react';

import Portal from '../portal/portal';
import IconClose from '../icons/close';
import IconLoader from '../icons/loader';
import IconSidepanelUnexpand from '../icons/sidepanel-unexpand';
import IconSidepanelExpand from '../icons/sidepanel-expand';
import { FooterContentPosition, SidePanelType, SidePanelTypes } from './types';

// hooks
import useAnimation from '../animations/use-animations';

// styles
import './styles.css';
import '../animations/animations.css';

export default function SidePanel({
  closeOnClickOutside = false,
  isOpen,
  onClose,
  children,
  childrenRef,
  footer,
  title = '',
  footerContentPosition = FooterContentPosition.Left,
  type = SidePanelType.Accounts,
  parent,
  className = '',
  footerVisible = true,
  errorSidepanel,
  checkInProgress,
  transactionCheckError,
  checkText,
  onExpandClick,
  sidepanelExpandedKey,
  withoutExpand,
  expanded,
}: SidePanelTypes): React.JSX.Element {
  const backdrop = useRef<HTMLDivElement | null>(null);

  const [expand, setExpand] = useState(false);

  const isActive = useAnimation({
    componentRef: backdrop,
    onClose,
    isOpen,
  });

  useEffect(() => {
    const expandedJSON = localStorage.getItem(sidepanelExpandedKey);

    if (expandedJSON) {
      const parsed = JSON.parse(expandedJSON);

      setExpand(parsed.expand);
    }
  }, [sidepanelExpandedKey, isOpen]);

  useEffect(() => {
    if (expanded) {
      setExpand(true);
    }
  }, [isOpen]);

  return (
    <Activity mode={isOpen || isActive ? 'visible' : 'hidden'}>
      <Portal className="side-panel-portal" parent={parent}>
        <div
          ref={backdrop}
          className={`animated-panel backdrop ${
            closeOnClickOutside ? 'fullscreen' : ''
          } ${isActive && isOpen ? 'active' : ''} ${className}`}
        >
          <div
            className={`animated-panel-content side-panel-content side-panel-${
              !expand ? type : SidePanelType.Expand
            }`}
          >
            <div className="side-panel-title">
              <div className="side-panel-title-left">
                {!withoutExpand && (
                  <button
                    type="button"
                    className="side-panel-expand-button"
                    onClick={() => {
                      setExpand(!expand);

                      localStorage.setItem(
                        sidepanelExpandedKey,
                        JSON.stringify({ expand: !expand }),
                      );

                      if (onExpandClick) {
                        onExpandClick();
                      }
                    }}
                    aria-label="Expand button"
                  >
                    {!expand ? (
                      <IconSidepanelUnexpand />
                    ) : (
                      <IconSidepanelExpand />
                    )}
                  </button>
                )}

                <span className="side-panel-title-text with-padding">
                  {title}
                </span>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="side-panel-title-close"
                aria-label="close"
              >
                <IconClose />
              </button>
            </div>

            <div className="side-panel-children" ref={childrenRef}>
              {children}
            </div>
            {errorSidepanel && (
              <>
                {typeof errorSidepanel === 'string' && (
                  <div className="side-panel-error">
                    <ul className="side-panel-errors-list">
                      <li>{errorSidepanel}</li>
                    </ul>
                  </div>
                )}
                {Array.isArray(errorSidepanel) && (
                  <div className="side-panel-error">
                    <ul className="side-panel-errors-list">
                      {errorSidepanel?.map((value, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>{`- ${value}`}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            {checkInProgress && (
              <span className="loading-text">
                <IconLoader />
                {checkText}
              </span>
            )}
            {transactionCheckError && (
              <div className="check-error">{transactionCheckError}</div>
            )}
            {footerVisible && footer && (
              <div className={`side-panel-footer ${footerContentPosition}`}>
                {footer}
              </div>
            )}
          </div>
        </div>
      </Portal>
    </Activity>
  );
}
